import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, SupportedLocale } from './languages';
// Assessment call sites carry no inline English defaults (unlike portal namespaces),
// so the en-US assessment strings must be present as a resource from boot.
import assessmentEnUS from './locales/en-US/assessment.json';
import { loadResourcesLocale, resourcesEnUS } from './resourcesLoader';
import { fetchActiveLanguages } from '../services/languages';

/**
 * Lazy-load a locale's namespace JSON.
 *
 * We do dynamic imports so we don't ship every language's translations
 * to every user. The bundle only fetches the JSON when a user actually
 * switches to that language.
 *
 * en-US is NOT in here for most namespaces — its strings live inline via
 * t('key', 'English default'). Exceptions: assessment + resources (bundled).
 *
 * en-SG DOES load: it ships override-only files holding just the ~100 strings
 * Singapore spells the British way (organisation, behaviour, centre). Anything
 * it omits falls through to the inline en-US default, so the two never diverge.
 */
async function loadLocale(locale: string, namespace: string): Promise<Record<string, unknown>> {
    if (namespace === 'resources') {
        return loadResourcesLocale(locale);
    }
    if (locale === 'en-US') return {};
    try {
        const mod = await import(`./locales/${locale}/${namespace}.json`);
        return mod.default ?? mod;
    } catch {
        return {};
    }
}

i18n
    .use(initReactI18next)
    .init({
        lng: DEFAULT_LOCALE,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: SUPPORTED_LOCALES,
        defaultNS: 'common',
        ns: ['common', 'nav', 'settings', 'people', 'positions', 'folders', 'teams', 'assessment', 'ariaChat', 'resources'],
        resources: {
            'en-US': { assessment: assessmentEnUS, resources: resourcesEnUS },
        },
        interpolation: {
            escapeValue: false, // React already escapes
        },
        returnNull: false,
        react: {
            useSuspense: false, // we'll load resources manually, no Suspense
        },
    });

/**
 * Add a namespace's resources for a given locale.
 * Called by changeLocale() when switching languages.
 */
async function ensureNamespaceLoaded(locale: string, namespace: string): Promise<void> {
    if (i18n.hasResourceBundle(locale, namespace)) return;
    const resources = await loadLocale(locale, namespace);
    i18n.addResourceBundle(locale, namespace, resources, true, true);
}

/**
 * Switch the active language. Loads any missing namespace JSON first.
 * Also updates <html lang="..."> for accessibility / screen readers.
 */
export async function changeLocale(locale: string): Promise<void> {
    const loadedNamespaces = (i18n.options.ns as string[]) ?? ['common'];
    await Promise.all(loadedNamespaces.map((ns) => ensureNamespaceLoaded(locale, ns)));
    await i18n.changeLanguage(locale);
    if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.lang = locale;
    }
}

/**
 * Read the active locale from the user object stored in localStorage.
 *
 * Resolution order:
 *   1. user.uiLanguage          (user's own preference)
 *   2. entity.defaultLanguage   (org default — when the user has no preference)
 *   3. en-US                    (final fallback)
 *
 * Falls back to DEFAULT_LOCALE if the stored value isn't a SupportedLocale.
 */
export function resolveLocaleFromStorage(): SupportedLocale {
    try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        const candidate: string | undefined =
            stored?.user?.uiLanguage ||
            stored?.user?.entity?.defaultLanguage;
        if (candidate && (SUPPORTED_LOCALES as string[]).includes(candidate)) {
            return candidate as SupportedLocale;
        }
    } catch {
        // ignore parse errors and fall through
    }
    return DEFAULT_LOCALE;
}

/**
 * resolveLocaleFromStorage, minus any switched-off language. An open session
 * keeps the old value in localStorage until it refreshes. Fails open.
 */
export async function resolveActiveLocaleFromStorage(): Promise<SupportedLocale> {
    const locale = resolveLocaleFromStorage();
    if (locale === DEFAULT_LOCALE) return DEFAULT_LOCALE;
    const active = await fetchActiveLanguages();
    return active.some((l) => l.locale === locale) ? locale : DEFAULT_LOCALE;
}

/**
 * Apply the resolved locale on app boot. Safe to call before any user data
 * is loaded — it will just resolve to DEFAULT_LOCALE.
 */
export async function bootstrapLocaleFromStorage(): Promise<void> {
    const locale = await resolveActiveLocaleFromStorage();
    if (locale !== DEFAULT_LOCALE) {
        await changeLocale(locale);
    }
}


export default i18n;
